import { PropsWithChildren, memo, useRef } from 'react';
// Loads the edit-capture subscribers so edits are recorded regardless of which
// versioning UI is mounted.
import '@/stores/versioning/editLog';
import { ClipsContextError, ClipsContextProvider, useClipsContext } from '../../../context/ClipsContext/ClipsContext';
import { TranscriptContextProvider, useTranscriptContext } from '../../../context/TranscriptContext/TranscriptContext';
import { SpeakerSegmentContextProvider } from '../../../context/SpeakerSegmentContext/SpeakerSegmentContext';
import { Error } from '@/domains/global';
import ErrorBoundaryForComponent from '@/App/ErrorBoundary/ErrorBoundaryForComponent';
import { ViewportStateProvider } from '@/components/atoms/ViewportStateProvider';
import { LazyMount } from '@/components/atoms/LazyMount';

interface ClipPlayerWrapperProps extends PropsWithChildren {
  clipId?: string;
  shared?: boolean;
  showLoaderOnPage?: boolean;
  handleTranscriptError: (_err: Error) => void;
  renderErrorComponent?: (error: ClipsContextError, clipId: string) => JSX.Element;
  isSavedSearchPage?: boolean;
  useNewSkeletonLoader?: boolean;
  isSmartEventsPage?: boolean;
  parentContext?: string;
}

const ClipPlayerWrapper: React.FC<ClipPlayerWrapperProps> = memo(
  ({
    clipId,
    shared,
    showLoaderOnPage,
    children,
    handleTranscriptError,
    renderErrorComponent,
    isSavedSearchPage,
    isSmartEventsPage,
    useNewSkeletonLoader = false,
    parentContext
  }) => {
    const { isTranscriptInitialized } = useTranscriptContext();
    if (!isTranscriptInitialized) {
      return null;
    }
    return (
      <ErrorBoundaryForComponent customError="Failed to load clip! Please try again after some time.">
        <ViewportStateProvider className="size-full">
          <LazyMount>
            <ClipsContextProvider
              clipId={clipId}
              shared={shared}
              showLoaderOnPage={showLoaderOnPage}
              renderErrorComponent={renderErrorComponent}
              isSavedSearchPage={isSavedSearchPage}
              useNewSkeletonLoader={useNewSkeletonLoader}
              isSmartEventsPage={isSmartEventsPage}
              parentContext={parentContext}
            >
              <ClipPlayerWrapperInternal
                isSavedSearchPage={isSavedSearchPage}
                handleTranscriptError={handleTranscriptError}
              >
                {children}
              </ClipPlayerWrapperInternal>
            </ClipsContextProvider>
          </LazyMount>
        </ViewportStateProvider>
      </ErrorBoundaryForComponent>
    );
  }
);

const ClipPlayerWrapperInternal: React.FC<
  Omit<ClipPlayerWrapperProps, 'clipId' | 'shared' | 'showLoaderOnPage' | 'renderErrorComponent'>
> = memo(({ children, handleTranscriptError }) => {
  const { clipData } = useClipsContext();

  const clipPlayerWrapperMountTime = useRef(performance.now());

  return (
    <TranscriptContextProvider
      broadcastId={clipData.broadcast}
      eventId={clipData.event}
      onErrorCallback={handleTranscriptError}
      shouldAssociateWithSharedAPI
      parentMountTime={clipPlayerWrapperMountTime}
      parentContext="ClipPlayerWrapperInternal"
    >
      <SpeakerSegmentContextProvider>{children}</SpeakerSegmentContextProvider>
    </TranscriptContextProvider>
  );
});

export default ClipPlayerWrapper;
